// GateFields — the console's "What do we already know?" block (RRA v2.1 A1),
// rendered at case open (CreateCaseModal) AND on Revise / first-record
// (AccommodationsPage ItemPanel) so the two can never ask different things.
//
//   - two <fieldset> groups: Yes · No · Not known yet, NO default selected;
//   - a one-line rationale after each (required only when that answer is Yes);
//   - the source of the knowledge (required when either answer is Yes) — the
//     "letter" option names the files already on file;
//   - "Credibly a trained service animal" only on a service-animal item;
//   - the standing rule line, always.
//
// The state and the validation are pure and live in lib/rraGateDraft.ts. There
// is no diagnosis field anywhere here: the only free text is the two bounded
// rationales. Words come from lib/rraCopy.ts (GATE).

import { useId } from 'react';
import { GATE } from '@/lib/rraCopy';
import { GATE_RATIONALE_MAX, gateSourceOptionLabel, type GateDraft } from '@/lib/rraGateDraft';
import type { RraGateAnswer, RraScope } from '@/lib/rraStaffClient';

const ANSWERS: RraGateAnswer[] = ['yes', 'no', 'not_known_yet'];

export function GateFields({
  value,
  onChange,
  letters,
  scope,
  errors,
  disabled = false,
}: {
  value: GateDraft;
  onChange: (next: GateDraft) => void;
  /** Filenames of the letters / documents already on file (the "letter" source's label). */
  letters: string[];
  scope: RraScope;
  /** What is still missing (lib/rraGateDraft validateGateDraft); shown under the block. */
  errors?: string[];
  disabled?: boolean;
}): React.JSX.Element {
  const uid = useId();
  const id = (name: string): string => `${uid}-${name}`;
  const set = (patch: Partial<GateDraft>): void => onChange({ ...value, ...patch });
  const anyYes = value.disabilityAnswer === 'yes' || value.needAnswer === 'yes';

  const question = (p: {
    key: 'disability' | 'need';
    legend: string;
    answer: RraGateAnswer | null;
    rationale: string;
    onAnswer: (a: RraGateAnswer) => void;
    onRationale: (text: string) => void;
  }): React.JSX.Element => {
    const rationaleRequired = p.answer === 'yes';
    return (
      <div className="stack-sm">
        <fieldset className="fieldset stack-sm">
          <legend>{p.legend}</legend>
          <div className="cluster" style={{ gap: 'var(--space-5)' }}>
            {ANSWERS.map((a) => (
              <label key={a} className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name={id(`${p.key}-answer`)}
                  value={a}
                  checked={p.answer === a}
                  disabled={disabled}
                  onChange={() => p.onAnswer(a)}
                />
                <span>{GATE.answers[a]}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="field">
          <label className="label" htmlFor={id(`${p.key}-rationale`)}>
            {GATE.rationaleLabel}
            {rationaleRequired && <span aria-hidden="true"> *</span>}
          </label>
          <input
            id={id(`${p.key}-rationale`)}
            className="input"
            maxLength={GATE_RATIONALE_MAX}
            value={p.rationale}
            required={rationaleRequired}
            aria-required={rationaleRequired}
            disabled={disabled}
            onChange={(e) => p.onRationale(e.target.value)}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="stack-sm" data-section="rra-gate-fields">
      <div style={{ fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-sm)' }}>{GATE.heading}</div>

      {question({
        key: 'disability',
        legend: GATE.disabilityQuestion,
        answer: value.disabilityAnswer,
        rationale: value.disabilityRationale,
        onAnswer: (a) => set({ disabilityAnswer: a }),
        onRationale: (t) => set({ disabilityRationale: t }),
      })}
      {question({
        key: 'need',
        legend: GATE.needQuestion,
        answer: value.needAnswer,
        rationale: value.needRationale,
        onAnswer: (a) => set({ needAnswer: a }),
        onRationale: (t) => set({ needRationale: t }),
      })}

      <div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-xs)' }}>{GATE.inlineRule}</div>

      <div className="field">
        <label className="label" htmlFor={id('source')}>
          {GATE.sourceLabel}
          {anyYes && <span aria-hidden="true"> *</span>}
        </label>
        <select
          id={id('source')}
          className="input"
          value={value.source}
          required={anyYes}
          aria-required={anyYes}
          disabled={disabled}
          onChange={(e) => set({ source: e.target.value as GateDraft['source'] })}
        >
          <option value="">Choose…</option>
          {GATE.sources.map((o) => (
            <option key={o.value} value={o.value}>
              {gateSourceOptionLabel(o.label, o.value === 'letter', letters)}
            </option>
          ))}
        </select>
      </div>

      {scope === 'service_animal' && (
        <label className="cluster" style={{ gap: 'var(--space-2)', fontSize: 'var(--text-sm)', alignItems: 'center', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={value.trainedServiceAnimal}
            disabled={disabled}
            onChange={(e) => set({ trainedServiceAnimal: e.target.checked })}
          />
          <span>{GATE.trainedServiceAnimal}</span>
        </label>
      )}

      {errors && errors.length > 0 && (
        <div role="alert" style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>
          {errors.map((e) => (
            <div key={e}>{e}</div>
          ))}
        </div>
      )}
    </div>
  );
}
